import { FC, Fragment, useCallback, useMemo } from 'react';

import Chip from 'components/_core/Chip';
import * as Form from 'components/_core/form';
import SunshineDropdown from 'components/_core/SunshineDropdown';

import locales from './locales';

export interface StatusDropdownOption {
  value: string;
  label: string;
}

interface StatusDropdownProps {
  selectedFilters: string[];
  options: StatusDropdownOption[];
  onStatusChange: (statuses: string[]) => void;
}

const StatusDropdown: FC<StatusDropdownProps> = ({
  onStatusChange,
  options,
  selectedFilters,
}) => {
  const handleSelectItem = useCallback(
    (value: string) => {
      if (selectedFilters.includes(value)) {
        onStatusChange(
          selectedFilters.filter((selectedFilter) => selectedFilter !== value),
        );
      } else {
        onStatusChange([...selectedFilters, value]);
      }
    },
    [selectedFilters, onStatusChange],
  );

  const listItems = useMemo(
    () =>
      options.map((option, index) => (
        <Fragment key={option.value}>
          {index !== 0 ? <SunshineDropdown.Divider /> : null}
          <SunshineDropdown.Item onClick={() => handleSelectItem(option.value)}>
            <Form.Checkbox
              isChecked={selectedFilters.includes(option.value)}
              pointerEvents="none"
            >
              <Form.Checkbox.Label>{option.label}</Form.Checkbox.Label>
            </Form.Checkbox>
          </SunshineDropdown.Item>
        </Fragment>
      )),
    [options, selectedFilters, handleSelectItem],
  );

  return (
    <SunshineDropdown closeOnSelect={false}>
      <SunshineDropdown.Trigger>
        <Chip
          icon="filter"
          iconRight="chevron-down"
          isActive={Boolean(selectedFilters.length)}
          width={160} // Keeps the dropdown static when the label changes
        >
          {selectedFilters.length
            ? locales.formatString(locales.filtersWithCount, {
                count: selectedFilters.length,
              })
            : locales.filters}
        </Chip>
      </SunshineDropdown.Trigger>

      <SunshineDropdown.List>{listItems}</SunshineDropdown.List>
    </SunshineDropdown>
  );
};

export default StatusDropdown;
